import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { taxService, shippingService, productService } from '../services/api';

// Estado inicial del carrito
const initialState = {
  items: [],
  subtotal: 0,
  tax: 0,
  shipping: 0,
  total: 0,
  itemCount: 0,
  isOpen: false,
  shippingAddress: null,
  taxBreakdown: null,
  shippingInfo: null,
  stockMessage: null // Mensaje cuando se intenta superar stock (solo al hacer clic en +)
};

// Tipos de acciones
const CART_ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  TOGGLE_CART: 'TOGGLE_CART',
  LOAD_CART: 'LOAD_CART',
  UPDATE_CALCULATIONS: 'UPDATE_CALCULATIONS',
  SET_SHIPPING_ADDRESS: 'SET_SHIPPING_ADDRESS',
  SET_STOCK_MESSAGE: 'SET_STOCK_MESSAGE',
  CLEAR_STOCK_MESSAGE: 'CLEAR_STOCK_MESSAGE'
};

// Reducer del carrito
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.ADD_ITEM: {
      const existingItem = state.items.find(
        item => item.id === action.payload.id && 
                item.selectedSize === action.payload.selectedSize &&
                item.selectedColor === action.payload.selectedColor
      );

      if (existingItem) {
        // Si el item ya existe, actualizar cantidad
        const updatedItems = state.items.map(item =>
          item.id === action.payload.id && 
          item.selectedSize === action.payload.selectedSize &&
          item.selectedColor === action.payload.selectedColor
            ? { ...item, quantity: item.quantity + action.payload.quantity }
            : item
        );
        
        const newSubtotal = calculateSubtotal(updatedItems);
        return {
          ...state,
          items: updatedItems,
          subtotal: newSubtotal,
          total: calculateTotal(updatedItems),
          itemCount: calculateItemCount(updatedItems)
        };
      } else {
        // Si es un item nuevo, agregarlo
        const newItems = [...state.items, action.payload];
        const newSubtotal = calculateSubtotal(newItems);
        return {
          ...state,
          items: newItems,
          subtotal: newSubtotal,
          total: calculateTotal(newItems),
          itemCount: calculateItemCount(newItems)
        };
      }
    }

    case CART_ACTIONS.REMOVE_ITEM: {
      const filteredItems = state.items.filter(
        item => !(item.id === action.payload.id && 
                  item.selectedSize === action.payload.selectedSize &&
                  item.selectedColor === action.payload.selectedColor)
      );
      
      const newSubtotal = calculateSubtotal(filteredItems);
      return {
        ...state,
        items: filteredItems,
        subtotal: newSubtotal,
        total: calculateTotal(filteredItems),
        itemCount: calculateItemCount(filteredItems)
      };
    }

    case CART_ACTIONS.UPDATE_QUANTITY: {
      const updatedItems = state.items.map(item =>
        item.id === action.payload.id && 
        item.selectedSize === action.payload.selectedSize &&
        item.selectedColor === action.payload.selectedColor
          ? { 
              ...item, 
              quantity: action.payload.quantity,
              stock: action.payload.stock ?? item.stock,
              maxStock: action.payload.stock ?? item.maxStock
            }
          : item
      );
      
      const newSubtotal = calculateSubtotal(updatedItems);
      return {
        ...state,
        items: updatedItems,
        subtotal: newSubtotal,
        total: calculateTotal(updatedItems),
        itemCount: calculateItemCount(updatedItems)
      };
    }

    case CART_ACTIONS.CLEAR_CART:
      return {
        ...state,
        items: [],
        subtotal: 0,
        tax: 0,
        shipping: 0,
        total: 0,
        itemCount: 0
      };

    case CART_ACTIONS.TOGGLE_CART:
      return {
        ...state,
        isOpen: !state.isOpen
      };

    case CART_ACTIONS.LOAD_CART:
      return {
        ...state,
        items: action.payload.items || [],
        subtotal: action.payload.subtotal || 0,
        tax: action.payload.tax || 0,
        shipping: action.payload.shipping || 0,
        total: action.payload.total || 0,
        itemCount: action.payload.itemCount || 0,
        taxBreakdown: action.payload.taxBreakdown || null,
        shippingInfo: action.payload.shippingInfo || null
      };
    
    case CART_ACTIONS.UPDATE_CALCULATIONS:
      return {
        ...state,
        subtotal: action.payload.subtotal,
        tax: action.payload.tax,
        shipping: action.payload.shipping,
        total: action.payload.total,
        taxBreakdown: action.payload.taxBreakdown,
        shippingInfo: action.payload.shippingInfo
      };
    
    case CART_ACTIONS.SET_SHIPPING_ADDRESS:
      return {
        ...state,
        shippingAddress: action.payload
      };

    case CART_ACTIONS.SET_STOCK_MESSAGE:
      return { ...state, stockMessage: action.payload };

    case CART_ACTIONS.CLEAR_STOCK_MESSAGE:
      return { ...state, stockMessage: null };

    default:
      return state;
  }
};

// Funciones auxiliares
const calculateSubtotal = (items) => {
  return items.reduce((total, item) => total + (item.price * item.quantity), 0);
};

const calculateItemCount = (items) => {
  return items.reduce((count, item) => count + item.quantity, 0);
};

// Función auxiliar actualizada (mantener compatibilidad)
const calculateTotal = (items) => {
  return calculateSubtotal(items);
};

/** Limpia ítems desde localStorage y evita objetos anidados enormes (p. ej. product completo). */
const normalizeCartItem = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id != null ? Number(raw.id) : null;
  if (id == null || Number.isNaN(id)) return null;
  const qty = Math.max(1, parseInt(raw.quantity, 10) || 1);
  const price = parseFloat(raw.price) || 0;
  const originalPrice =
    raw.originalPrice != null ? parseFloat(raw.originalPrice) : null;
  const img =
    (Array.isArray(raw.images) && raw.images[0]) ||
    raw.image ||
    '/images/placeholders/placeholder-product.png';
  const fulfillment =
    raw.fulfillment === 'supplier' ? 'supplier' : 'javiland';
  const weightGrams = Math.max(
    1,
    parseInt(raw.weightGrams ?? raw.weight_grams, 10) || 500
  );
  return {
    id,
    productId: raw.productId != null ? Number(raw.productId) : id,
    name: String(raw.name || 'Producto').trim() || 'Producto',
    price,
    originalPrice: originalPrice != null && !Number.isNaN(originalPrice) ? originalPrice : null,
    image: img,
    selectedSize: raw.selectedSize || 'Único',
    selectedColor: raw.selectedColor || 'Único',
    quantity: qty,
    stock: raw.stock != null ? Number(raw.stock) : undefined,
    maxStock: raw.maxStock != null ? Number(raw.maxStock) : undefined,
    fulfillment,
    weightGrams
  };
};

// Crear el contexto
const CartContext = createContext();

// Hook personalizado para usar el contexto
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe ser usado dentro de un CartProvider');
  }
  return context;
};

// Proveedor del contexto
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const skipNextPersist = useRef(true);

  // Cargar carrito desde localStorage al inicializar
  useEffect(() => {
    const savedCart = localStorage.getItem('javiland-cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        const rawItems = Array.isArray(parsedCart.items) ? parsedCart.items : [];
        const items = rawItems.map(normalizeCartItem).filter(Boolean);
        const subtotal = calculateSubtotal(items);
        dispatch({
          type: CART_ACTIONS.LOAD_CART,
          payload: {
            ...parsedCart,
            items,
            subtotal,
            total: parsedCart.total != null ? parsedCart.total : subtotal,
            itemCount: calculateItemCount(items)
          }
        });
      } catch (error) {
        console.error('Error al cargar el carrito:', error);
        localStorage.removeItem('javiland-cart');
      }
    }
  }, []);

  // Guardar carrito en localStorage cuando cambie
  useEffect(() => {
    // Evita sobrescribir localStorage en el primer ciclo con estado vacío antes de LOAD_CART
    if (skipNextPersist.current) {
      skipNextPersist.current = false;
      return;
    }
    const cartData = {
      items: state.items.map((it) => normalizeCartItem(it)).filter(Boolean),
      subtotal: state.subtotal,
      tax: state.tax,
      shipping: state.shipping,
      total: state.total,
      itemCount: state.itemCount,
      taxBreakdown: state.taxBreakdown,
      shippingInfo: state.shippingInfo
    };
    try {
      localStorage.setItem('javiland-cart', JSON.stringify(cartData));
    } catch (e) {
      console.error('No se pudo guardar el carrito:', e);
    }
  }, [state.items, state.subtotal, state.tax, state.shipping, state.total, state.itemCount, state.taxBreakdown, state.shippingInfo]);

  // Funciones del carrito
  const addToCart = async (product, selectedSize, selectedColor, quantity = 1) => {
    try {
      // Si el producto tiene un mapeo de talla a ID (para camisetas agrupadas), usar el ID correcto
      let productIdToUse = product.id;
      if (product.sizeToIdMap && selectedSize && selectedSize !== 'Único' && product.sizeToIdMap[selectedSize]) {
        productIdToUse = product.sizeToIdMap[selectedSize];
        console.log(`📦 Usando ID específico para talla ${selectedSize}: ${productIdToUse} (en lugar de ${product.id})`);
      }
      
      // Consultar stock real desde la API usando el ID correcto
      const stockResponse = await productService.checkStock(productIdToUse);
      const availableStock = stockResponse.success ? (stockResponse.stock ?? stockResponse.data?.stock ?? 0) : 0;
      const isDropshipping =
        stockResponse.fulfillment === 'dropshipping' ||
        product.isDropshipping ||
        product.fulfillment === 'dropshipping';

      if (availableStock <= 0 && !isDropshipping) {
        alert(`⚠️ Este producto no está disponible en este momento.`);
        return;
      }

      // Dropshipping: disponible aunque stock=0 (se pide al proveedor)
      const effectiveStock = isDropshipping ? 99 : availableStock;

      const currentStock = product.variants?.find(
        v => v.size === selectedSize && v.color === selectedColor
      )?.stock || effectiveStock;
      
      // Verificar stock total disponible (usar el ID correcto para filtrar)
      const totalInCart = state.items
        .filter(item => item.id === productIdToUse)
        .reduce((sum, item) => sum + item.quantity, 0);
      
      const requestedTotal = totalInCart + quantity;
      const maxAvailable = isDropshipping ? effectiveStock : Math.min(availableStock, currentStock);
      
      if (requestedTotal > maxAvailable) {
        const available = maxAvailable - totalInCart;
        if (available <= 0) {
          alert(`⚠️ Solo hay ${maxAvailable} unidad(es) disponible(s). Ya tienes el máximo en tu carrito.`);
          return;
        } else {
          alert(`⚠️ Solo puedes agregar ${available} unidad(es) más. El máximo disponible es ${maxAvailable} unidades.`);
          quantity = available;
        }
      }
      
      if (quantity > currentStock) {
        alert(`⚠️ Solo hay ${currentStock} unidad(es) disponible(s) de esta variante.`);
        quantity = Math.min(quantity, currentStock);
      }
      
      if (quantity <= 0) {
        return;
      }

      // Usar el mismo ID que ya calculamos arriba (productIdToUse)
      const fulfillment = ['supplier', 'dropshipping'].includes(product.fulfillment)
        ? product.fulfillment
        : (isDropshipping ? 'dropshipping' : 'javiland');
      const weightGrams = Math.max(
        1,
        parseInt(product.weightGrams ?? product.weight_grams, 10) || 500
      );
      const cartItem = {
        id: productIdToUse, // Usar el ID correcto según la talla
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice || product.regularPrice || null, // Precio original si hay descuento
        image: product.images && product.images.length > 0 ? product.images[0] : (product.image || '/images/placeholders/placeholder-product.png'),
        selectedSize,
        selectedColor,
        quantity,
        productId: productIdToUse, // Usar el ID correcto según la talla
        stock: maxAvailable,
        maxStock: maxAvailable, // Agregar maxStock para compatibilidad
        fulfillment,
        weightGrams
      };

      dispatch({ type: CART_ACTIONS.CLEAR_STOCK_MESSAGE });
      dispatch({ type: CART_ACTIONS.ADD_ITEM, payload: cartItem });
    } catch (error) {
      console.error('Error verificando stock:', error);
      alert('⚠️ Error al verificar disponibilidad. Por favor, intenta de nuevo.');
    }
  };

  const removeFromCart = (itemId, selectedSize, selectedColor) => {
    dispatch({ 
      type: CART_ACTIONS.REMOVE_ITEM, 
      payload: { id: itemId, selectedSize, selectedColor } 
    });
  };

  const updateQuantity = async (itemId, selectedSize, selectedColor, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId, selectedSize, selectedColor);
      return;
    }

    try {
      // Consultar stock real desde la API
      const stockResponse = await productService.checkStock(itemId);
      const availableStock = stockResponse.success ? (stockResponse.stock ?? stockResponse.data?.stock ?? 0) : 0;

      // Detectar dropshipping (igual que en addToCart)
      const isDropshipping =
        stockResponse.fulfillment === 'dropshipping' ||
        state.items.find(i => i.id === itemId)?.fulfillment === 'dropshipping';

      // Solo eliminar si no hay stock Y no es dropshipping
      if (availableStock <= 0 && !isDropshipping) {
        removeFromCart(itemId, selectedSize, selectedColor);
        return;
      }

      const effectiveStock = isDropshipping ? 99 : availableStock;

      const item = state.items.find(
        i => i.id === itemId &&
             i.selectedSize === selectedSize &&
             i.selectedColor === selectedColor
      );

      if (item) {
        const totalInCart = state.items
          .filter(i => !(i.id === itemId && i.selectedSize === selectedSize && i.selectedColor === selectedColor))
          .filter(i => i.id === itemId)
          .reduce((sum, i) => sum + i.quantity, 0);
        
        const requestedTotal = totalInCart + quantity;
        
        if (requestedTotal > effectiveStock) {
          dispatch({ type: CART_ACTIONS.SET_STOCK_MESSAGE, payload: `No hay más unidades disponibles. Máximo: ${effectiveStock} unidad(es).` });
          return;
        }
      }

      dispatch({ type: CART_ACTIONS.CLEAR_STOCK_MESSAGE });
      dispatch({
        type: CART_ACTIONS.UPDATE_QUANTITY,
        payload: { id: itemId, selectedSize, selectedColor, quantity, stock: effectiveStock }
      });
    } catch (error) {
      console.error('Error verificando stock:', error);
    }
  };

  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
  };

  const toggleCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_STOCK_MESSAGE });
    dispatch({ type: CART_ACTIONS.TOGGLE_CART });
  };

  const clearStockMessage = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_STOCK_MESSAGE });
  };

  const openCart = () => {
    if (!state.isOpen) {
      dispatch({ type: CART_ACTIONS.TOGGLE_CART });
    }
  };

  const closeCart = () => {
    if (state.isOpen) {
      dispatch({ type: CART_ACTIONS.TOGGLE_CART });
    }
  };

  // Verificar si un producto está en el carrito
  const isInCart = (productId, selectedSize, selectedColor) => {
    return state.items.some(
      item => item.id === productId && 
              item.selectedSize === selectedSize &&
              item.selectedColor === selectedColor
    );
  };

  // Obtener la cantidad de un producto en el carrito
  const getCartItemQuantity = (productId, selectedSize, selectedColor) => {
    const item = state.items.find(
      item => item.id === productId && 
              item.selectedSize === selectedSize &&
              item.selectedColor === selectedColor
    );
    return item ? item.quantity : 0;
  };

  // Calcular impuestos del carrito
  const calculateTaxes = async () => {
    if (state.items.length === 0) {
      return { success: false, message: 'Carrito vacío' };
    }

    try {
      // Preparar items para el API
      const items = state.items.map(item => ({
        product: {
          id: item.id,
          name: item.name,
          basePrice: item.price, // Asumiendo que price ya tiene el precio correcto
          taxCategory: item.taxCategory || 'standard',
          ivaRate: item.ivaRate || 19
        },
        quantity: item.quantity
      }));

      const taxCalculation = await taxService.calculateOrderTaxes(items);
      
      if (taxCalculation.success) {
        return {
          success: true,
          data: taxCalculation
        };
      }

      return { success: false, message: 'Error calculando impuestos' };
    } catch (error) {
      console.error('Error calculando impuestos:', error);
      return { success: false, message: error.message };
    }
  };

  // Calcular envío
  const calculateShipping = async (addressData) => {
    if (!addressData || !addressData.city || !addressData.department) {
      return { success: false, message: 'Dirección incompleta' };
    }

    try {
      const subtotal = calculateSubtotal(state.items);

      const shippingItems = state.items.map((item) => ({
        productId: item.productId ?? item.id,
        quantity: item.quantity,
        fulfillment: item.fulfillment === 'supplier' ? 'supplier' : 'javiland',
        weightGrams: Math.max(1, item.weightGrams ?? item.weight ?? 500)
      }));

      const javilandWeightPreview = state.items.reduce((total, item) => {
        const f = item.fulfillment === 'supplier' ? 'supplier' : 'javiland';
        if (f !== 'javiland') return total;
        const w = Math.max(1, item.weightGrams ?? item.weight ?? 500);
        return total + w * item.quantity;
      }, 0);
      const legacyWeight = javilandWeightPreview > 0 ? javilandWeightPreview : 500;

      const shippingCalculation = await shippingService.calculateShipping(
        {
          ...addressData,
          subtotal
        },
        legacyWeight,
        'sobre',
        shippingItems
      );

      if (shippingCalculation.success) {
        // API: { success, message, data: { shipping, isFreeShipping, ... } }
        const payload =
          shippingCalculation.data && typeof shippingCalculation.data === 'object'
            ? shippingCalculation.data
            : shippingCalculation;
        return {
          success: true,
          data: payload
        };
      }

      return { success: false, message: 'Error calculando envío' };
    } catch (error) {
      console.error('Error calculando envío:', error);
      return { success: false, message: error.message };
    }
  };

  // Actualizar todos los cálculos (impuestos + envío)
  const updateCalculations = async (shippingAddress) => {
    try {
      const subtotal = calculateSubtotal(state.items);
      
      // Calcular impuestos
      const taxResult = await calculateTaxes();
      const tax = taxResult.success ? taxResult.data.totalTax : 0;
      const taxBreakdown = taxResult.success ? taxResult.data : null;
      
      // Calcular envío si hay dirección
      let shipping = 0;
      let shippingInfo = null;
      
      if (shippingAddress) {
        const shippingResult = await calculateShipping(shippingAddress);
        if (shippingResult.success) {
          shipping = shippingResult.data.shipping?.finalCost || 0;
          shippingInfo = shippingResult.data;
        }
      }
      
      const total = subtotal + tax + shipping;
      
      dispatch({
        type: CART_ACTIONS.UPDATE_CALCULATIONS,
        payload: {
          subtotal,
          tax,
          shipping,
          total,
          taxBreakdown,
          shippingInfo
        }
      });
      
      return { success: true, subtotal, tax, shipping, total };
    } catch (error) {
      console.error('Error actualizando cálculos:', error);
      return { success: false, message: error.message };
    }
  };

  // Establecer dirección de envío
  const setShippingAddress = (address) => {
    dispatch({
      type: CART_ACTIONS.SET_SHIPPING_ADDRESS,
      payload: address
    });
  };

  const value = {
    ...state,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    toggleCart,
    openCart,
    closeCart,
    clearStockMessage,
    isInCart,
    getCartItemQuantity,
    calculateTaxes,
    calculateShipping,
    updateCalculations,
    setShippingAddress
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};






















